import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Paperclip, TriangleAlert } from 'lucide-react';
import { NOTICE_CHANNEL_COPY, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, Chip, ErrorNote, SectionTitle, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { useMediaUrl } from '@/lib/media-url';
import { TranslateBlock } from '@/components/TranslateBlock';
import { formatFullDate } from '@/lib/format';
import { useRole } from '@/store/auth';
import type { Notice } from '@/types';

interface ReadReport {
  total: number;
  readCount: number;
  unread: { id: string; name: string; phone: string | null }[];
}

export function NoticeDetailPage() {
  const { id = '' } = useParams();
  const role = useRole();
  const isStaff = role === ROLES.DIRECTOR || role === ROLES.TEACHER;

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['notices', id],
    queryFn: () => apiData<Notice>(`/notices/${id}`),
  });

  // 필독 공지의 읽음 확인 집계 — 교직원만 (§5.4)
  const readReport = useQuery({
    queryKey: ['notices', id, 'reads'],
    queryFn: () => apiData<ReadReport>(`/notices/${id}/reads`),
    enabled: isStaff && Boolean(data?.mustRead),
  });

  if (isPending) return <Spinner />;
  if (isError || !data) {
    return (
      <Screen withNav={false}>
        <AppBar title="공지" back />
        <ErrorNote message="공지를 불러오지 못했어요." onRetry={() => void refetch()} />
      </Screen>
    );
  }

  return (
    <Screen withNav={false}>
      <AppBar title="공지사항" back />

      <div className="flex flex-col gap-4 p-4">
        <Card>
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {data.isUrgent && (
              <span className="inline-flex items-center gap-1 rounded-full bg-notice/10 px-2 py-0.5 text-[12px] font-semibold text-notice">
                <TriangleAlert size={12} /> 긴급
              </span>
            )}
            {data.mustRead && <Chip tone="accent">필독</Chip>}
            {data.classRoom && <Chip tone="primary">{data.classRoom.name}</Chip>}
            {/* 문자·이메일로도 안내한 공지 (§5.4 v1.22) — 긴급은 정책상 둘 다 나가므로 따로 표시하지 않는다 */}
            {!data.isUrgent &&
              (data.extraChannels ?? []).map((channel) => (
                <Chip key={channel} tone="info">
                  {NOTICE_CHANNEL_COPY.sentBadge[channel]}
                </Chip>
              ))}
          </div>

          <h2 className="text-xl font-bold">{data.title}</h2>
          <p className="mt-1 text-sm text-ink-sub">{formatFullDate(data.publishedAt)}</p>
          <p className="mt-4 whitespace-pre-wrap text-base leading-relaxed">{data.content}</p>
          <TranslateBlock text={data.content} />
        </Card>

        {data.attachments.length > 0 && (
          <section>
            <SectionTitle>첨부파일</SectionTitle>
            <ul className="flex flex-col gap-2">
              {data.attachments.map((attachment) => (
                <AttachmentRow key={attachment.id} mediaId={attachment.media.id} />
              ))}
            </ul>
          </section>
        )}

        {isStaff && data.mustRead && readReport.data && (
          <section>
            <SectionTitle>읽음 현황</SectionTitle>
            <Card>
              <p className="tnum text-base">
                {readReport.data.readCount} / {readReport.data.total}명 확인
              </p>
              {readReport.data.unread.length > 0 && (
                <>
                  <p className="mt-3 text-sm font-medium text-ink-sub">아직 확인하지 않은 분</p>
                  <ul className="mt-1 flex flex-wrap gap-1.5">
                    {readReport.data.unread.map((person) => (
                      <li key={person.id}>
                        <Chip tone="neutral">{person.name}</Chip>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>
          </section>
        )}
      </div>
    </Screen>
  );
}

function AttachmentRow({ mediaId }: { mediaId: string }) {
  const { data } = useMediaUrl(mediaId);

  return (
    <li>
      <a
        href={data?.url ?? '#'}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 rounded-sm border border-line bg-surface px-3 py-3 text-base"
      >
        <Paperclip size={16} className="text-ink-sub" />
        <span className="truncate">첨부파일 열기</span>
      </a>
    </li>
  );
}
